import { z } from 'zod';
import {
  reviewMaxLength,
  reviewMinLength,
  reviewTitleMaxLength,
  reviewTitleMinLength,
} from './constants';

export const ReviewRequestMessageSchema = z.object({
  url: z.string(),
});
export type ReviewRequestMessage = z.infer<typeof ReviewRequestMessageSchema>;

export enum ReviewType {
  WORK_NOT_CARRIED = 'WORK_NOT_CARRIED',
  WORK_CARRIED = 'WORK_CARRIED',
}

export const WorkNotCarriedOutReviewNotRecommendedReason = {
  'Company warning': 'COMPANY_WARNING',
  'County court judgement': 'COUNTY_COURT_JUDGEMENT',
  'Courtesy or attitude': 'COURTESY_OR_ATTITUDE',
  'Missed appointment': 'MISSED_APPOINTMENT',
} as const;

export const WorkCarriedOutReviewNotRecommendedReason = {
  Aftercare: 'AFTERCARE',
  'Aggressive or threatening behaviour': 'AGGRESSIVE_OR_THREATENING_BEHAVIOUR',
  Assault: 'ASSAULT',
  'Call out charge': 'CALL_OUT_CHARGE',
  'Communication issues': 'COMMUNICATION_ISSUES',
  'Company warning': 'COMPANY_WARNING',
  'Cold call': 'COLD_CALL',
  'Contract issue': 'CONTRACT_ISSUE',
  Convictions: 'CONVICTIONS',
  'Courtesy or attitude': 'COURTESY_OR_ATTITUDE',
  'Deposit issues': 'DEPOSIT_ISSUES',
  'Invoice not received': 'INVOICE_NOT_RECEIVED',
  'No guarantee or warranty': 'NO_GUARANTEE_OR_WARRANTY',
  'Misinformed Or mislead': 'MISINFORMED_OR_MISLEAD',
  'Order disagreement': 'ORDER_DISAGREEMENT',
  'Over charging': 'OVER_CHARGING',
  'Poor workmanship': 'POOR_WORKMANSHIP',
  'Reliability or timekeeping': 'RELIABILITY_OR_TIMEKEEPING',
  Tidiness: 'TIDINESS',
  Other: 'OTHER',
} as const;

export enum ReviewStatus {
  NOT_PUBLISHED = 'NOT_PUBLISHED',
  PUBLISHED = 'PUBLISHED',
  SCHEDULED = 'SCHEDULED',
  DO_NOT_PUBLISH = 'DO_NOT_PUBLISH',
  PENDING = 'PENDING',
  PARKED = 'PARKED',
  ARCHIVED = 'ARCHIVED',
}

export enum ReviewVerified {
  Verified = 'VERIFIED',
  Pending = 'PENDING',
  Failed = 'FAILED',
  NotVerified = 'NOT_VERIFIED',
}

export const RatingSchema = z.object({
  qualityOfWorkmanship: z.number().min(0).max(10).optional(),
  tidiness: z.number().min(0).max(10).optional(),
  reliabilityAndTimekeeping: z.number().min(0).max(10).optional(),
  courtesy: z.number().min(0).max(10).optional(),
  rating: z.number().min(0).max(10).optional(),
  communication: z.number().min(0).max(10).optional(),
});

export const ReviewerSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  postcode: z.string(),
  phone: z.string(),
  email: z.string().email(),
});

export const SharedSchema = z.object({
  companyId: z.number(),
  title: z
    .string()
    .min(reviewTitleMinLength, 'Enter a review title')
    .max(
      reviewTitleMaxLength,
      `Review title must be ${reviewTitleMaxLength} characters or less`,
    ),
  review: z
    .string()
    .min(reviewMinLength, 'Enter your review')
    .max(
      reviewMaxLength,
      `Your review must be ${reviewMaxLength} characters or less`,
    ),
  reviewer: ReviewerSchema.optional(),
  jobId: z.string().uuid().optional(),
  consumerId: z.string().optional(),
  consumerEmail: z.string().email().optional(),
  cfTurnstileResponse: z.string().optional(),
  mediaAttachmentIds: z.array(z.string()).optional(),
  tradeContact: z.literal(null),
  publishFeedback: z.literal(true),
});

const ReviewSchema = SharedSchema.omit({
  reviewer: true,
  jobId: true,
  consumerEmail: true,
  cfTurnstileResponse: true,
}).extend({
  id: z.string(),
  status: z.nativeEnum(ReviewStatus),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  verified: z.nativeEnum(ReviewVerified),
  rating: RatingSchema,
  type: z.nativeEnum(ReviewType),
  name: z.string(),
  uniqueName: z.string(),
  location: z.object({
    postcode: z.string().optional(),
  }),
  reply: z
    .object({
      reply: z.string().optional(),
    })
    .optional(),
});

export const SharedSchemaPublic = SharedSchema.omit({ reviewer: true }).extend({
  reviewer: ReviewerSchema,
});

export const SharedResponseSchema = SharedSchema.omit({
  consumerId: true,
}).extend({
  id: z.string(),
  status: z.nativeEnum(ReviewStatus),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const SharedResponseSchemaPublic = SharedSchema.omit({
  consumerId: true,
}).extend({
  id: z.string(),
  status: z.nativeEnum(ReviewStatus),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const WorkNotCarriedOutSchema = z.object({
  type: z.literal(ReviewType.WORK_NOT_CARRIED),
  averageRating: z.number().min(0).max(10),
});

export const WorkCarriedOutSchema = z.object({
  type: z.literal(ReviewType.WORK_CARRIED),
  categoryId: z.number(),
  topLevelCategoryId: z.number().optional(),
  workCompletedAt: z.string().datetime(),
  rating: RatingSchema,
  valueOfWork: z.number().optional(),
});

const RecommendedTradeSchema = z.object({
  recommendTrade: z.literal(true),
});

const NotRecommendedTradeBaseSchema = z.object({
  recommendTrade: z.literal(false),
  notRecommendedReason: z.union([
    z.nativeEnum(WorkNotCarriedOutReviewNotRecommendedReason),
    z.nativeEnum(WorkCarriedOutReviewNotRecommendedReason),
  ]),
});

const createReviewSchema = (shared: z.ZodObject<any, any, any>) =>
  z.union([
    // work carried out
    shared.merge(RecommendedTradeSchema).merge(WorkCarriedOutSchema),

    shared.merge(NotRecommendedTradeBaseSchema).merge(WorkCarriedOutSchema),

    // work not carried out
    shared.merge(RecommendedTradeSchema).merge(WorkNotCarriedOutSchema),

    shared.merge(NotRecommendedTradeBaseSchema).merge(WorkNotCarriedOutSchema),
  ]);

export const ReviewRequestSchema = createReviewSchema(SharedSchema);
export const ReviewPublicRequestSchema = createReviewSchema(SharedSchemaPublic);

export const ReviewResponseSchema = createReviewSchema(SharedResponseSchema);
export const ReviewResponsePublicSchema = createReviewSchema(
  SharedResponseSchemaPublic,
);

export type Review = z.infer<typeof ReviewSchema>;

export const GetReviewsResponseSchema = z.object({
  data: z.array(ReviewSchema),
  page: z.number(),
  size: z.number(),
  total: z.number(),
});

export const GetLatestReviewResponseSchema = z.object({
  data: z.record(z.number(), z.boolean()),
});

export type ReviewRequest = z.infer<typeof ReviewRequestSchema>;
export type ReviewRequestPublic = z.infer<typeof ReviewPublicRequestSchema>;

export type ReviewResponse = z.infer<typeof ReviewResponseSchema>;

export type GetReviewsResponse = z.infer<typeof GetReviewsResponseSchema>;
